export type BackendRuntimeConfig = {
  enabled: boolean
  apiBaseUrl: string | null
  source: 'runtime' | 'build' | 'development' | 'local' | 'preview'
  error: string | null
}

export type BackendRuntimeConfigInput = {
  runtime?: Record<string, unknown> | null
  viteApiBaseUrl?: unknown
  development?: boolean
  locationHref?: string
}

declare global {
  interface Window {
    __PULSE_RUNTIME_CONFIG__?: {
      apiBaseUrl?: unknown
    }
  }
}

type ImportMetaWithEnv = ImportMeta & {
  readonly env?: {
    readonly DEV?: boolean
    readonly VITE_API_BASE_URL?: string
  }
}

export class BackendNotConfiguredError extends Error {
  readonly code = 'BACKEND_NOT_CONFIGURED'

  constructor(message = '仅界面预览：未配置服务端 API 地址') {
    super(message)
    this.name = 'BackendNotConfiguredError'
  }
}

export function resolveBackendRuntimeConfig(
  input: BackendRuntimeConfigInput
): BackendRuntimeConfig {
  const runtime = input.runtime
  const hasRuntimeValue = Boolean(runtime)
    && Object.prototype.hasOwnProperty.call(runtime, 'apiBaseUrl')
  const buildValue = typeof input.viteApiBaseUrl === 'string'
    ? input.viteApiBaseUrl.trim()
    : ''
  const localOrigin = loopbackOrigin(input.locationHref)
  const source = hasRuntimeValue
    ? 'runtime' as const
    : buildValue
      ? 'build' as const
      : input.development
        ? 'development' as const
        : localOrigin
          ? 'local' as const
          : 'preview' as const
  const candidate = hasRuntimeValue
    ? runtime?.apiBaseUrl
    : buildValue || (input.development ? pageOrigin(input.locationHref) : localOrigin)

  if (candidate === undefined || candidate === null || candidate === '') {
    return {
      enabled: false,
      apiBaseUrl: null,
      source: 'preview',
      error: null
    }
  }
  if (typeof candidate !== 'string') {
    return disabledConfig(source, '服务端 API 地址必须是字符串')
  }
  const trimmed = candidate.trim()
  if (!trimmed) {
    return {
      enabled: false,
      apiBaseUrl: null,
      source: 'preview',
      error: null
    }
  }
  if (trimmed === 'self') {
    const origin = pageOrigin(input.locationHref)
    if (!origin) return disabledConfig(source, '无法从当前页面解析同源服务端地址')
    return {
      enabled: true,
      apiBaseUrl: origin,
      source,
      error: null
    }
  }
  try {
    const url = new URL(trimmed)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return disabledConfig(source, '服务端 API 地址仅支持 http 或 https')
    }
    if (
      url.username
      || url.password
      || url.hostname.includes('*')
      || (url.pathname !== '' && url.pathname !== '/')
      || url.search
      || url.hash
    ) {
      return disabledConfig(source, '服务端 API 地址必须是无凭据、路径、查询或锚点的精确 Origin')
    }
    return {
      enabled: true,
      apiBaseUrl: url.origin,
      source,
      error: null
    }
  } catch {
    return disabledConfig(source, '服务端 API 地址格式无效')
  }
}

export function resolveApiUrl(
  path: string,
  config: BackendRuntimeConfig = backendRuntimeConfig
) {
  if (!config.enabled || !config.apiBaseUrl) {
    throw new BackendNotConfiguredError(config.error || undefined)
  }
  const base = new URL(config.apiBaseUrl)
  const resolved = new URL(path, `${base.origin}/`)
  if (
    (resolved.protocol !== 'http:' && resolved.protocol !== 'https:')
    || resolved.origin !== base.origin
  ) {
    throw new Error('API 请求地址必须与已配置服务端同源')
  }
  return resolved.toString()
}

export function resolveWebSocketUrl(
  config: BackendRuntimeConfig = backendRuntimeConfig
) {
  const url = new URL(resolveApiUrl('/v1/ws', config))
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}

export function readBackendRuntimeConfig(): BackendRuntimeConfig {
  const viteEnv = (import.meta as ImportMetaWithEnv).env
  return resolveBackendRuntimeConfig({
    runtime: typeof window === 'undefined'
      ? null
      : window.__PULSE_RUNTIME_CONFIG__ ?? null,
    viteApiBaseUrl: viteEnv?.VITE_API_BASE_URL,
    development: viteEnv?.DEV === true,
    locationHref: typeof window === 'undefined'
      ? undefined
      : window.location.href
  })
}

export const backendRuntimeConfig = readBackendRuntimeConfig()

function disabledConfig(
  source: BackendRuntimeConfig['source'],
  error: string
): BackendRuntimeConfig {
  return {
    enabled: false,
    apiBaseUrl: null,
    source,
    error
  }
}

function pageOrigin(locationHref: string | undefined) {
  if (!locationHref) return ''
  try {
    const url = new URL(locationHref)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.origin : ''
  } catch {
    return ''
  }
}

function loopbackOrigin(locationHref: string | undefined) {
  if (!locationHref) return ''
  try {
    const url = new URL(locationHref)
    const hostname = url.hostname.toLowerCase()
    return (
      url.protocol === 'http:' || url.protocol === 'https:'
    ) && (
      hostname === 'localhost'
      || hostname === '127.0.0.1'
      || hostname === '[::1]'
    )
      ? url.origin
      : ''
  } catch {
    return ''
  }
}
